/* eslint-disable max-lines-per-function */
import React, { FC, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { ChipAutoComplete, ChipAutoCompleteData, Description, H3, Icon, IconTypesKeys } from "@evergis/ui";
import { useTranslation } from "react-i18next";
import { DetailedTableInfoDc } from "@evergis/api";

import { DialogTitle } from "../../DialogComponents/DialogTitle";
import { FullScreenDialog } from "../../DialogComponents/FullScreenDialog";
import { RestrictedWidthContainer } from "../../DialogComponents/styled";
import { DialogActions } from "../../DialogComponents/DialogActions";
import { getAclRole, getNameByRole } from "../getAclRole";
import { AccessRoleGroup } from "../types";
import { useUsersAndRoles } from "../useUsersAndRoles";
import { TableIconContainer, TableInformation, TableSubtitle, TableTitle, TableTitleContainer } from "./styled";
import { CommonSettingsControls, CommonSettingsTitleContainer } from "../styled";
import { AccessControl } from "../components/AccessControl";

import { updatePermissions } from "../../../../resources/bulkOperations";
import { getUsername } from "../../../../ducks/user";
import { isConfigure, isRead, isWrite } from "../../../../utils/permissions";

import { useDialogInfo, useDialogReset } from "../../../../hooks/useDialogs";
import { useStructure } from "./hooks/useStructure";
import { useAccessNotifications } from "../hooks/useAccessNotifications";
import { useTableInfo } from "./hooks/useTableInfo";
import { useContext } from "../hooks/useContext";

export const TableAccess: FC = () => {
  const {
    resources: [tableName],
  } = useDialogInfo(true);

  const { t } = useTranslation("dialogs");
  const context = useContext();
  const [update, updateState] = updatePermissions.use();
  const closeDialog = useDialogReset();
  const currentUser: string = useSelector(getUsername);
  const { tableStructure, loading: structureLoading } = useTableInfo(tableName);
  const { users, usersAndRolesLoading, autoCompleteProps, pickedUsers, setPickedUsers } = useUsersAndRoles();
  const [activeUser, setActiveUser] = useState<string>("");
  const { alias: tableAlias } = tableStructure || {};
  const loading = structureLoading || usersAndRolesLoading;

  useAccessNotifications(updateState);

  const {
    structure,
    onUserPermissionsInit,
    onUserPermissionsRemove,
    onUpdateTableAcl,
    enrichAclData,
    currentTablePermissions,
  } = useStructure(tableStructure as DetailedTableInfoDc, activeUser, loading);

  // Коллбэк, вызываемый при клике на кнопку "Сохранить".
  // Производим запрос на обновление acl таблицы, после чего
  // дергаем эндпоинт добавления таблицы в каталоги пользователей
  const onApply = useCallback(() => {
    if (!structure) {
      return;
    }

    update({
      input: {
        tables: [
          {
            objectName: structure.name,
            data: structure.acl.data,
          },
        ],
      },
    }).finally(closeDialog);
  }, [structure, update, closeDialog, pickedUsers]);

  // Обновление списка пользователей, которые необходимо отразить в списке чипов,
  // при изменении данных в структуре таблицы (acl)
  useEffect(() => {
    if (!structure || !users || !currentUser) {
      return;
    }

    let processedUsers = [];

    structure.acl.data.forEach(tableAclItem => {
      const { role } = tableAclItem;
      const userName = getNameByRole(role, users);

      if (processedUsers.includes(userName)) {
        return;
      }

      if (
        users.some(user => user.aclRole === role) &&
        `__${currentUser}` !== role &&
        isConfigure(structure.permissions)
      ) {
        if (userName === AccessRoleGroup.Public) {
          processedUsers = [userName, ...processedUsers];
        } else {
          processedUsers = [...processedUsers, userName];
        }
      }
    });

    setPickedUsers(processedUsers);
  }, [users, currentUser, structure]);

  const getTextName = useCallback((name: string) => (name === AccessRoleGroup.Public ? t("access.public") : name), [t]);
  const readOnly = activeUser === AccessRoleGroup.Public;

  return (
    <FullScreenDialog dialogContext={context}>
      <DialogTitle
        titleText={t("access.dialogHeader")}
        style={{ paddingBottom: 0 }}
        extra={<Description style={{ marginTop: 0 }}>{tableAlias}</Description>}
      >
        <RestrictedWidthContainer>
          <H3 style={{ marginBottom: 0 }}>{t("access.usersInvited")}</H3>
          <Description>{t("access.usersInvitedTableDescription")}</Description>
          <ChipAutoComplete
            loading={loading}
            activeItem={activeUser}
            items={users as ChipAutoCompleteData[]}
            activeItems={pickedUsers}
            onSelectItem={(name: string) => {
              setActiveUser(name);
              onUserPermissionsInit(getAclRole(name, users));
            }}
            onRemoveItem={(name: string) => {
              setActiveUser("");
              onUserPermissionsRemove(getAclRole(name, users));
            }}
            onActiveItem={(name: string) => {
              setActiveUser(name);
              enrichAclData(getAclRole(name, users));
            }}
            slideButtonTitle={t("access.slide")}
            listButtonTitle={t("access.list")}
            placeholder={t("access.chooseUserName")}
            searchPlaceholder={t("access.search")}
            getTextName={getTextName}
            autoCompleteProps={autoCompleteProps}
          />
          {activeUser && (
            <>
              <CommonSettingsTitleContainer>
                <H3>{t("access.accessTitle")}</H3>
                <CommonSettingsControls>
                  <AccessControl
                    type="read"
                    active={isRead(currentTablePermissions)}
                    clickable={!isRead(currentTablePermissions)}
                    onClick={() => !isRead(currentTablePermissions) && onUpdateTableAcl("read")}
                  />
                  {!readOnly && (
                    <>
                      <AccessControl
                        type="write"
                        active={isWrite(currentTablePermissions)}
                        onClick={() => onUpdateTableAcl("write")}
                      />
                      <AccessControl
                        type="configure"
                        active={isConfigure(currentTablePermissions)}
                        onClick={() => onUpdateTableAcl("configure")}
                      />
                    </>
                  )}
                </CommonSettingsControls>
              </CommonSettingsTitleContainer>
              {structure && (
                <TableInformation>
                  <TableIconContainer>
                    <Icon kind={`geom_${structure.geometries}` as IconTypesKeys} />
                  </TableIconContainer>
                  <TableTitleContainer>
                    <TableTitle>{structure.alias || structure.name}</TableTitle>
                    <TableSubtitle>
                      {t("access.objects")}: {structure.rowCount || 0}
                      <span style={{ display: "inline-block", width: "1rem" }} />
                      {t("access.owner")}: {structure.owner || " "}
                    </TableSubtitle>
                  </TableTitleContainer>
                </TableInformation>
              )}
            </>
          )}
        </RestrictedWidthContainer>
      </DialogTitle>
      <DialogActions isWaiting={loading || updateState.loading} onApply={onApply} />
    </FullScreenDialog>
  );
};
